import { Component, Host, h, Prop, Watch, State } from '@stencil/core';
import { t } from '../../i18n';

@Component({
  tag: 'sy-avatar',
  styleUrl: 'scss/index.scss',
  shadow: true,
})
export class Avatar {
  private initialsContent: string;

  @State() showInitials: boolean;

  /**
   * Sets avatar shape to circle.
   */
  @Prop({ reflect: true }) rounded = false;

  /**
   * Sets avatar size to small (32x32).
   */
  @Prop({ reflect: true }) small = false;

  /**
   * Sets avatar background-image (URL).
   */
  @Prop() image: string;
  @Watch('image')
  imageWatcher(): void {
    this.updateShowInitials();
  }

  /**
   * Sets a text its shown if there is no `image`.
   * Must be a string with same size of `initialsSize`.
   */
  @Prop() initials: string;
  @Watch('initials')
  initialsWatcher(): void {
    this.updateInitialsContent();
    this.updateShowInitials();
  }

  /**
   * Sets `initials` string characters number (min: 1, max: 3)
   */
  @Prop({ reflect: true, mutable: true }) initialsSize = 2;
  @Watch('initialsSize')
  initialsSizeWatcher(): void {
    this.updateInitialSize();
    this.updateInitialsContent();
    this.updateShowInitials();
  }

  /**
   * Provides alternative text for the avatar image.
   */
  @Prop({ reflect: true }) alternativeText: string;

  componentWillLoad() {
    this.updateInitialSize();
    this.updateInitialsContent();
    this.updateShowInitials();
  }

  private updateInitialSize() {
    if (this.initialsSize < 1) {
      this.initialsSize = 1;
    }

    if (this.initialsSize > 3) {
      this.initialsSize = 3;
    }
  }

  private updateInitialsContent() {
    this.initialsContent = this.initials;

    if (this.initials && this.initials.length > this.initialsSize) {
      this.initialsContent = this.initials.slice(0, this.initialsSize);
    }
  }

  private updateShowInitials() {
    this.showInitials = !this.image && Boolean(this.initialsContent);
  }

  private renderInitialsContent(): string | undefined {
    if (this.showInitials) {
      return this.initialsContent;
    }
  }

  private renderImage(): HTMLImageElement | undefined {
    if (this.image) {
      return (
        <img
          alt={this.alternativeText ?? t('USER_AVATAR', 'user avatar')}
          src={this.image}
          onError={() => {
            this.showInitials = Boolean(this.initialsContent);
          }}
        />
      );
    }
  }

  render() {
    return <Host>{this.renderInitialsContent() || this.renderImage()}</Host>;
  }
}
